import { SealCheck, Tag, User, Lock, ArrowRight } from "@phosphor-icons/react"
import { Heading } from "@tokenterminal/core/ui/heading/Heading"
import { Badge } from "@tokenterminal/core/ui/messaging/badge/Badge"
import { blurhashToCssGradientString } from "@unpic/placeholder"
import { useAuth } from "context/Auth"
import DashboardImage from "modules/studio/components/DashboardImage"
import Link from "next/link"
import { useMemo } from "react"
import { type DashboardWithTag } from "../views/types"

const Overlay = () => (
  <div
    className="absolute inset-0 z-[1] rounded-[15px]"
    style={{
      boxShadow: "inset 0px 0px 0px 1px var(--border-default)",
      background: `radial-gradient(at bottom left,
          hsl(var(--bg-surface) / 0.95) 0%,
          hsl(var(--bg-surface) / 0.8) 50%,
          hsl(var(--bg-surface) / 0.5) 90%,
          hsl(var(--bg-surface) / 0.45) 95%,
          hsl(var(--bg-surface) / 0.4) 100%)`,
    }}
  />
)

export function Featured({ dashboard }: { dashboard: DashboardWithTag }) {
  const { user, isLoginFinished } = useAuth()

  const isLoggedIn = user?.id && isLoginFinished
  const mustBeLoggedIn = !isLoggedIn && dashboard.login_required

  const tags = useMemo(() => dashboard.tags.map((tag) => tag.name), [dashboard])
  const blurredCoverImageGradient = dashboard.cover_image
    ? blurhashToCssGradientString(dashboard.cover_image)
    : ""

  return (
    <Link
      href={`/studio/dashboards/${dashboard.user_dashboard_id}`}
      className="relative flex flex-col justify-between gap-6 p-7 min-h-[380px] min-w-[300px] flex-1"
    >
      <div
        className="absolute inset-0 z-[1] rounded-2xl"
        style={{
          background: blurredCoverImageGradient,
        }}
      />
      <Overlay />
      <Badge
        variant="solid"
        size="small"
        className="!text-[10px] font-bold tracking-widest uppercase z-[2] self-start !bg-white !text-neutral-900"
      >
        Featured
      </Badge>
      <div
        className="flex gap-8 items-start xl:items-end justify-between flex-col xl:flex-row"
        style={{ zIndex: 2 }}
      >
        <div className="flex flex-col gap-5">
          <DashboardImage
            size={50}
            imageAlt={dashboard.name || "logo"}
            logoURL={dashboard.logo_url}
          />
          <div className="flex flex-col gap-3 max-w-full">
            <div className="flex flex-col gap-1">
              <Heading level={2} className="text-balance">
                {dashboard?.name}
              </Heading>
              <p className="text-sm opacity-60 max-w-[600px]">
                {dashboard?.description}
              </p>
            </div>
            <div className="hidden md:flex gap-5 justify-start">
              <div className="flex gap-1.5 items-center flex-shrink-0">
                <User size="12" className="opacity-60" />
                <div className="flex gap-1 items-center justify-between">
                  <div className="text-xs opacity-60">
                    {dashboard.created_by}
                  </div>
                  {dashboard.is_official && (
                    <SealCheck
                      size="12"
                      weight="fill"
                      className="text-fg-emerald"
                    />
                  )}
                </div>
              </div>
              {tags.length > 0 ? (
                <div className="flex items-center gap-1.5 flex-shrink-0 opacity-60">
                  <Tag size="12" />
                  <div className="text-xs">
                    {tags[0]} {tags.length > 1 && `+ ${tags.length - 1} more`}
                  </div>
                </div>
              ) : (
                <div />
              )}
            </div>
          </div>
        </div>
        {mustBeLoggedIn ? (
          <div className="flex items-center gap-2 opacity-75 shrink-0">
            <Lock size={12} />
            <span className="text-xs">Login required</span>
          </div>
        ) : (
          <div className="flex items-center gap-2 opacity-75 shrink-0">
            <span className="text-sm">View dashboard</span>{" "}
            <ArrowRight size={14} />
          </div>
        )}
      </div>
    </Link>
  )
}
